import type { AssessmentAnswerPayload } from "../axios/quiz";

export const ASSESSMENT_PROTOCOL_VERSION = 2;

const newId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const createAssessmentSaveId = newId;
export const createAssessmentVisitId = newId;
export const createAssessmentSegmentId = newId;

type QuestionSaveMeta = {
  visitId: string;
  revision: number;
};

const visitMetaByKey = new Map<string, QuestionSaveMeta>();
const saveQueues = new Map<string, Promise<unknown>>();
const pendingPayloadByKey = new Map<string, AssessmentAnswerPayload>();

const questionKey = (assessmentId: string, questionIndex: number) =>
  `${assessmentId}:${questionIndex}`;

export const beginQuestionVisit = (
  assessmentId: string,
  questionIndex: number,
): string => {
  const key = questionKey(assessmentId, questionIndex);
  const previous = visitMetaByKey.get(key);
  const visitId = createAssessmentVisitId();
  visitMetaByKey.set(key, {
    visitId,
    revision: previous?.revision ?? 0,
  });
  return visitId;
};

export const getQuestionVisitId = (
  assessmentId: string,
  questionIndex: number,
): string => {
  const key = questionKey(assessmentId, questionIndex);
  const existing = visitMetaByKey.get(key);
  if (existing?.visitId) {
    return existing.visitId;
  }
  return beginQuestionVisit(assessmentId, questionIndex);
};

export const getQuestionRevision = (
  assessmentId: string,
  questionIndex: number,
): number => visitMetaByKey.get(questionKey(assessmentId, questionIndex))?.revision ?? 0;

export const setQuestionRevision = (
  assessmentId: string,
  questionIndex: number,
  revision: number,
): void => {
  const key = questionKey(assessmentId, questionIndex);
  const existing = visitMetaByKey.get(key);
  visitMetaByKey.set(key, {
    visitId: existing?.visitId ?? createAssessmentVisitId(),
    revision: Math.max(0, Math.floor(revision)),
  });
};

export const clearAssessmentSaveProtocol = (assessmentId?: string | null) => {
  if (!assessmentId) {
    visitMetaByKey.clear();
    saveQueues.clear();
    pendingPayloadByKey.clear();
    return;
  }
  const prefix = `${assessmentId}:`;
  for (const key of [...visitMetaByKey.keys()]) {
    if (key.startsWith(prefix)) visitMetaByKey.delete(key);
  }
  for (const key of [...saveQueues.keys()]) {
    if (key.startsWith(prefix)) saveQueues.delete(key);
  }
  for (const key of [...pendingPayloadByKey.keys()]) {
    if (key.startsWith(prefix)) pendingPayloadByKey.delete(key);
  }
};

export const rememberPendingSavePayload = (
  assessmentId: string,
  questionIndex: number,
  payload: AssessmentAnswerPayload,
) => {
  pendingPayloadByKey.set(questionKey(assessmentId, questionIndex), payload);
};

export const takePendingSavePayload = (
  assessmentId: string,
  questionIndex: number,
): AssessmentAnswerPayload | null => {
  const key = questionKey(assessmentId, questionIndex);
  return pendingPayloadByKey.get(key) ?? null;
};

export const clearPendingSavePayload = (
  assessmentId: string,
  questionIndex: number,
) => {
  pendingPayloadByKey.delete(questionKey(assessmentId, questionIndex));
};

// Serialize saves per question so revisions stay ordered.
export const enqueueQuestionSave = async <T>(
  assessmentId: string,
  questionIndex: number,
  task: () => Promise<T>,
): Promise<T> => {
  const key = questionKey(assessmentId, questionIndex);
  const previous = saveQueues.get(key) ?? Promise.resolve();
  const run = previous.catch(() => undefined).then(task);
  saveQueues.set(
    key,
    run.then(
      () => undefined,
      () => undefined,
    ),
  );
  return run;
};
